<template>
  <div class="interface-management app-page app-page--fill">
    <el-card>
      <div class="page-header">
        <div class="page-actions">
          <el-button v-if="can('新增')" type="primary" :icon="Plus" @click="openCreateDialog">新增</el-button>
          <el-button
            v-if="can('列表')"
            type="primary"
            circle
            size="small"
            class="app-refresh-btn"
            :icon="Refresh"
            @click="loadData"
            :loading="loading"
            title="刷新"
          />
        </div>
      </div>

      <DynamicTable
        ref="dynamicTableRef"
        :data="tableData"
        :model="tableModel"
        :loading="loading"
        :max-height="tableMaxHeight"
        :show-column-selector="true"
        :show-export="false"
        :show-edit-button="can('编辑')"
        :show-delete-button="can('删除')"
        :pagination="paginationConfig"
        :column-labels="columnLabels"
        @edit="handleEdit"
        @delete="handleDelete"
        @filter="handleFilter"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
    </el-card>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogMode === 'create' ? '新增接口' : '修改接口'"
      width="640px"
      :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px">
        <el-form-item label="接口名称" prop="Name">
          <el-input v-model="formData.Name" placeholder="请输入接口名称" />
        </el-form-item>
        <el-form-item label="接口路径" prop="LinkUrl">
          <el-input v-model="formData.LinkUrl" placeholder="/api/接口管理/列表查询" />
        </el-form-item>
        <el-form-item label="启用" prop="Enabled">
          <el-switch v-model="formData.Enabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button v-if="can('保存')" type="primary" :loading="saving" @click="handleSubmit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import DynamicTable from '@/components/DynamicTable.vue'
import { PermissionManagement } from '@/network'
import { useButtonPermission } from '@/utils/buttonPermission'

const { can } = useButtonPermission('/QuanXianGuanLi/jiekou.vue')

interface InterfaceItem {
  Id?: string
  Name?: string
  LinkUrl?: string
  Enabled?: boolean
  CreateId?: number
  CreateBy?: string
  CreateTime?: string
  ModifyTime?: string
  IsDeleted?: boolean
}

const dynamicTableRef = ref<InstanceType<typeof DynamicTable> | null>(null)
const loading = ref(false)
const saving = ref(false)
const tableData = ref<InterfaceItem[]>([])
const tableModel = ref<Record<string, string> | undefined>(undefined)
const filterParams = ref<any[]>([])

const currentPage = ref(1)
const pageSize = ref(15)
const total = ref(0)

// 表格最大高度
const tableMaxHeight = ref<number>(500)

// 计算表格最大高度
const calculateTableHeight = () => {
  nextTick(() => {
    const windowHeight = window.innerHeight
    tableMaxHeight.value = windowHeight - 280
  })
}

const paginationConfig = reactive({
  show: true,
  page: currentPage.value,
  pageSize: pageSize.value,
  get total() { return total.value },
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
})

// 表头字段中文映射
const columnLabels: Record<string, string> = {
  Name: '接口名称',
  LinkUrl: '接口路径',
  Enabled: '启用状态',
  CreateBy: '创建人',
  CreateTime: '创建时间',
  ModifyTime: '修改时间'
}

const dialogVisible = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const formRef = ref()
const formData = reactive<InterfaceItem>({
  Id: undefined,
  Name: '',
  LinkUrl: '',
  Enabled: true
})

const formRules = {
  Name: [{ required: true, message: '请输入接口名称', trigger: 'blur' }],
  LinkUrl: [{ required: true, message: '请输入接口路径', trigger: 'blur' }]
}

const loadData = async () => {
  try {
    loading.value = true
    const res: any = await PermissionManagement.interfaceList(
      { page: currentPage.value, intPageSize: pageSize.value },
      filterParams.value
    )
    if (res && res.success && res.status === 200 && res.response) {
      tableData.value = Array.isArray(res.response.data) ? res.response.data : []
      total.value = res.response.dataCount || tableData.value.length
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model
      } else {
        tableModel.value = undefined
      }
    } else {
      tableData.value = []
      total.value = 0
      tableModel.value = undefined
      ElMessage.error(res?.msg || '数据加载失败')
    }
  } catch (error) {
    console.error('加载接口列表失败:', error)
    tableData.value = []
    total.value = 0
    tableModel.value = undefined
    ElMessage.error('加载接口列表失败')
  } finally {
    loading.value = false
  }
}

const handleFilter = (filters: any[]) => {
  filterParams.value = filters
  currentPage.value = 1
  loadData()
}

const handlePageChange = (page: number) => {
  currentPage.value = page
  loadData()
}

const handlePageSizeChange = (size: number) => {
  pageSize.value = size
  currentPage.value = 1
  loadData()
}

const resetForm = () => {
  formData.Id = undefined
  formData.Name = ''
  formData.LinkUrl = ''
  formData.Enabled = true
}

const openCreateDialog = () => {
  dialogMode.value = 'create'
  resetForm()
  dialogVisible.value = true
}

const handleEdit = (row: InterfaceItem) => {
  dialogMode.value = 'edit'
  Object.assign(formData, row)
  dialogVisible.value = true
}

const handleDelete = (row: InterfaceItem) => {
  if (!row.Id) {
    ElMessage.warning('缺少接口ID，无法删除')
    return
  }
  ElMessageBox.confirm('确定要删除该接口吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      try {
        const res: any = await PermissionManagement.interfaceDelete({ id: row.Id })
        if (res && res.success && res.status === 200) {
          ElMessage.success('删除成功')
          loadData()
        } else {
          ElMessage.error(res?.msg || '删除失败')
        }
      } catch (error) {
        console.error('删除接口失败:', error)
        ElMessage.error('删除失败')
      }
    })
    .catch(() => {})
}

const handleSubmit = () => {
  formRef.value?.validate(async (valid: boolean) => {
    if (!valid) return
    try {
      saving.value = true
      const isCreate = dialogMode.value === 'create'
      const res: any = isCreate
        ? await PermissionManagement.interfaceCreate({ ...formData })
        : await PermissionManagement.interfaceUpdate({ ...formData })
      if (res && res.success && res.status === 200) {
        ElMessage.success(isCreate ? '新增成功' : '更新成功')
        dialogVisible.value = false
        loadData()
      } else {
        ElMessage.error(res?.msg || (isCreate ? '新增失败' : '更新失败'))
      }
    } catch (error) {
      console.error('保存接口失败:', error)
      ElMessage.error('保存失败')
    } finally {
      saving.value = false
    }
  })
}

onMounted(() => {
  loadData()
  calculateTableHeight()
  window.addEventListener('resize', calculateTableHeight)
})

onUnmounted(() => {
  window.removeEventListener('resize', calculateTableHeight)
})
</script>

<style scoped lang="less">
.interface-management {
  min-height: 0;

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .page-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }

    .page-actions {
      display: flex;
      gap: 8px;
    }
  }
}
</style>
